/**
 * useSessionStore — JWT/session lifecycle for the authenticated user.
 *
 * Part of the useAuthStore split (architecture review item 4). Owns:
 *   • Access/refresh token plumbing (sessionStorage/localStorage glue)
 *   • `isAuthenticated`, `isFetchingUser`, `isRefreshingUser`, `isUserLoaded`
 *   • `getUserInformation()` orchestration — fans out to userStore +
 *     entitlementsStore via their respective setter actions.
 *   • `logoutUser()` — clears tokens, wipes all three stores, wipes the
 *     pf_* persisted state, shows the logged-out toast.
 *
 * Profile fields live in `useUserStore`; permissions/entitlements live in
 * `useEntitlementsStore`. `useAuthStore` is a thin facade over all three.
 */

import { identifyAnalyticsUser, resetAnalyticsUser } from '@/composables/useAnalytics';
import { clearAllPersistedState } from '@/composables/usePersistedState';
import i18n from '@/i18n';
import { showToast } from '@/plugins/toast';
import { redirectToLoginOnSessionExpired } from '@/repositories/clients/sharedTokenRefresh';
import AuthRepository from '@/repositories/AuthRepository';
import { defineStore } from 'pinia';
import { useEntitlementsStore } from './useEntitlementsStore';
import { useUserStore } from './useUserStore';

const CACHED_USER_KEY = 'cachedCurrentUser';

function readCachedUser() {
  try {
    const cached = localStorage.getItem(CACHED_USER_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
}

function writeCachedUser(payload) {
  try {
    localStorage.setItem(
      CACHED_USER_KEY,
      JSON.stringify({ ...payload, cachedAt: Date.now() })
    );
  } catch {
    // ignore quota / private-mode failures
  }
}

function clearCachedUser() {
  try {
    localStorage.removeItem(CACHED_USER_KEY);
  } catch {
    // ignore
  }
}

function normalizeUserResponse(data) {
  return {
    ...data,
    public_address: data.public_address,
    isSuperUser: data.is_superuser,
    createdOn: data.created_on,
    lastLogin: data.last_login,
    receiveUpdates: data.receive_updates,
    receiveNewsletter: data.receive_newsletter,
    requiresTwoFactor: data.requires_two_factor,
  };
}

function parseJwt(token) {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join('')
  );
  return JSON.parse(jsonPayload);
}

function isValidJwt(token) {
  if (!token || token.split('.').length < 3) {
    return { status: false, reason: 'tokenInvalid' };
  }
  let data;
  try {
    data = parseJwt(token);
  } catch {
    // A stale/corrupt stored token isn't valid JSON/base64 — treat as invalid, don't throw.
    return { status: false, reason: 'tokenInvalid' };
  }
  if (data.exp < Date.now() / 1000) {
    return { status: false, reason: 'tokenExpired' };
  }
  return { status: true, reason: 'tokenValid' };
}

export const useSessionStore = defineStore('session', {
  state: () => ({
    isAuthenticated: false,
    isFetchingUser: false,
    isRefreshingUser: false,
    isUserLoaded: false,
  }),

  getters: {
    getRefreshToken: () => () => localStorage.getItem('refreshToken'),
  },

  actions: {
    clearTokens() {
      sessionStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      clearCachedUser();
    },

    setAccessToken({ accessToken }) {
      sessionStorage.setItem('accessToken', accessToken);
      const tokenStatus = isValidJwt(accessToken);
      this.isAuthenticated = tokenStatus.status;
    },

    setRefreshToken({ refreshToken }) {
      localStorage.setItem('refreshToken', refreshToken);
    },

    setIsAuthenticated(payload) {
      this.isAuthenticated = payload;
    },

    setFetchingUser(payload) {
      this.isFetchingUser = payload;
    },

    setRefreshingUser(payload) {
      this.isRefreshingUser = payload;
    },

    setUserLoaded(loaded) {
      this.isUserLoaded = loaded;
      if (loaded) {
        sessionStorage.setItem('isUserLoaded', 'true');
      } else {
        sessionStorage.removeItem('isUserLoaded');
      }
    },

    applyUserPayload(payload) {
      const userStore = useUserStore();
      const entitlementsStore = useEntitlementsStore();
      writeCachedUser(payload);
      userStore.setUserData(payload);
      entitlementsStore.setEntitlementData(payload);
      identifyAnalyticsUser(payload);
      this.isAuthenticated = true;
      // Eagerly hydrate the granular access set (/api/v2/me/access) so the
      // new entitlement layer is live alongside the legacy claim data —
      // the token chip, upgrade flow and `useAccess` gates resolve without
      // waiting for a consumer to mount. Best-effort + fire-and-forget:
      // fetchAccess swallows errors and preserves the last-known-good set,
      // so a slow/failed call never blocks login.
      //
      // Only when we actually hold a token to send it with. `hydrateCachedUser`
      // reaches here at boot for a returning visitor whose `refreshToken` +
      // `cachedCurrentUser` survived in localStorage while `accessToken` died
      // with the tab; firing there issues an unauthenticated request that can
      // only 401, and fetchAccess treats a 401 as a full session teardown. An
      // expired-but-present token still fires — the client interceptor
      // refreshes it and retries. `getUserInformation` calls us again with a
      // live token immediately after, so nothing is lost by waiting.
      if (sessionStorage.getItem('accessToken')) {
        entitlementsStore.fetchAccess();
      }
    },

    expireSession() {
      this.setUserLoaded(false);
      this.clearTokens();
      this.clearUserData();
      showToast(i18n.global.t('messages.session_expired'));
      redirectToLoginOnSessionExpired();
    },

    async getUserInformation(options = {}) {
      const { silent = false } = options;

      if (
        sessionStorage.getItem('accessToken') &&
        isValidJwt(sessionStorage.getItem('accessToken'))
      ) {
        this.isAuthenticated = true;
      }

      if (!sessionStorage.getItem('accessToken') && !localStorage.getItem('refreshToken')) {
        return false;
      }

      const cached = readCachedUser();
      if (cached && this.isUserLoaded) {
        if (!this.isRefreshingUser) {
          this.isRefreshingUser = true;
          try {
            const response = await AuthRepository.userInformation();
            if (response) {
              const data = normalizeUserResponse(response.data);
              this.applyUserPayload(data);
              this.setUserLoaded(true);
              if (isValidJwt(sessionStorage.getItem('accessToken'))) {
                this.setIsAuthenticated(true);
              }
              return true;
            }
          } catch (error) {
            if (error.response?.status === 401) {
              this.expireSession();
            }
            return false;
          } finally {
            this.isRefreshingUser = false;
          }
        }
        return true;
      }

      if (this.isFetchingUser || this.isRefreshingUser) {
        return false;
      }

      if (silent) this.isRefreshingUser = true;
      else this.isFetchingUser = true;

      try {
        const response = await AuthRepository.userInformation();
        if (response) {
          const data = normalizeUserResponse(response.data);
          this.applyUserPayload(data);
          this.setUserLoaded(true);
          if (isValidJwt(sessionStorage.getItem('accessToken'))) {
            this.setIsAuthenticated(true);
          }
          return true;
        }
      } catch (error) {
        if (error.response?.status === 401) {
          this.expireSession();
        }
        return false;
      } finally {
        if (silent) this.isRefreshingUser = false;
        else this.isFetchingUser = false;
      }
    },

    hydrateCachedUser() {
      const cached = readCachedUser();
      if (!cached) return false;
      if (!sessionStorage.getItem('accessToken') && !localStorage.getItem('refreshToken')) {
        return false;
      }
      this.applyUserPayload(cached);
      const accessToken = sessionStorage.getItem('accessToken');
      const tokenStatus = accessToken ? isValidJwt(accessToken) : { status: false };
      this.setIsAuthenticated(tokenStatus.status);
      return true;
    },

    clearUserData() {
      const userStore = useUserStore();
      const entitlementsStore = useEntitlementsStore();
      userStore.clearUserData();
      entitlementsStore.clearEntitlements();
      this.isAuthenticated = false;
      this.isUserLoaded = false;
      sessionStorage.removeItem('isUserLoaded');
      clearCachedUser();
    },

    async logoutUser() {
      // Best-effort server-side session invalidation. Must run BEFORE we drop
      // the access token (the call is authenticated), and must never block
      // local logout — an expired token, 404, or offline state still results
      // in a clean local sign-out.
      try {
        await AuthRepository.logout();
      } catch {
        // Ignore: proceed with local cleanup regardless.
      }
      this.clearTokens();
      this.clearUserData();
      clearAllPersistedState();
      resetAnalyticsUser();
      showToast(i18n.global.t('messages.logged_out'));
    },

    async refreshToken() {
      // No-op stub matching the legacy Vuex action signature; the actual
      // token-refresh deduplication happens in sharedTokenRefresh.js.
    },
  },
});
